import {computed, ComputedRef, Ref, ref, ToRef} from "vue";
import {autobind} from "@/decorators/autobind";
import ViewController from "@/interfaces/ViewController";
// @ts-ignore
import AppController from '@target/components/App/ts/AppController';
import PaymentService, {createPaymentPayload} from "@/services/operations/payment/PaymentService";
import PurchaseRepository from "@/repositories/purchase/PurchaseRepository";
import AgentsAmountRanges from "@models/errors/components/AgentsAmountRanges";
import LightPaymentPidResponseError from "@models/responses/light-payment-pid/LightPaymentPidResponseError";
import PurchaseOperation from "@models/operations/PurchaseOperation";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import ServiceAccount from "@/services/v2/data/service-account/ServiceAccount";
import ModelAccount from "@models/v2/account/ModelAccount";
import DocumentPurchase from "@/services/firebase/firestore/documents/DocumentPurchase";
import {DocumentTypes} from "@/services/firebase/firestore/documents/DocumentTypes";
import RouterService from "@/services/RouterService";

export default class NewPaymentController implements ViewController {
    private static instance?: NewPaymentController;
    private _purchaseRepository: PurchaseRepository = new PurchaseRepository();
    private _paymentService: PaymentService;
    private _routerService: RouterService;
    private _isCanSelectAgent = ref<boolean>(Boolean(import.meta.env.VITE_PURCHASE_IS_CAN_SELECT_AGENT));
    private _minAmountEnabled = ref<boolean>(Boolean(import.meta.env.VITE_PURCHASE_MIN_AMOUNT_ENABLED));
    private _minAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_MIN_AMOUNT));
    private _defaultAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_DEFAULT_AMOUNT));
    private _defaultExtWFlowAmount = ref<number>(Number.parseInt(import.meta.env.VITE_PURCHASE_DEFAULT_EXT_W_FLOW_AMOUNT));
    private _account = ServiceAccount.of().account as Ref<ModelAccount>;
    private readonly _currency: Ref<any>;
    public agentsAmountRanges: Ref<AgentsAmountRanges | null> = ref(null);
    public isPaymentSystemsLoading: Ref<boolean> = ref(false);
    public inputCurrency: Ref<string>;
    public amount: Ref<number>;
    public btnContinueLoading: Ref<boolean>;
    public minMaxRangesAmount: ComputedRef<{ min: number, max: number}>
    public textMaxMinRangeError = ref(false);

    static getInstance() {
        if (typeof this.instance === 'undefined') {
            this.instance = new NewPaymentController();
        }
        return this.instance;
    }

    private constructor() {
        this._paymentService = AppController.getInstance().paymentService;
        this._routerService = AppController.getInstance().routerService;
        const payment = this._paymentService.payment.value as PurchaseOperation;

        this._paymentService = AppController.getInstance().paymentService;
        this._currency = ref(ServiceAccount.of().account.value?.currency);
        this.inputCurrency = ref(this._currency.value.name);

        this.amount = ref(payment.amount!);
        this.btnContinueLoading = ref(false);

        this.setPurchaseFirebaseConfig();
        this.updateAgentsAmountRanges();

        this.minMaxRangesAmount = computed(() => {
            if (this.agentsAmountRanges.value === null) return { min: 0, max: 0 };
            const {min, max} = this.agentsAmountRanges.value;

            if (!min || !max) return { min: 0, max: 0 };

            return { min: Math.ceil(min), max: Math.floor(max) };
        });
    }

    get currency(): Ref<any> {
        return this._currency;
    }

    get paymentService(): PaymentService {
        return this._paymentService;
    }

    get routerService(): RouterService {
        return this._routerService;
    }

    public calculateStartAmountValue(): number {
        this.setPurchaseFirebaseConfig();

        const {buy} = this._account.value!.currency.rates;

        if (this.agentsAmountRanges.value == null) {
            return Math.ceil(this._defaultAmount.value * buy);
        } else {
            return Math.ceil(this.agentsAmountRanges.value!.min!);
        }
    }

    private setPurchaseFirebaseConfig() {
        const doc = FirebaseService.of().firestoreService?.getDocument<DocumentPurchase>(DocumentTypes.PURCHASE);
        if (doc) {
            if (doc.isCanSelectAgent) {
                this._isCanSelectAgent.value = doc.isCanSelectAgent!;
            }

            if (doc.minAmountEnabled) {
                this._minAmountEnabled.value = doc.minAmountEnabled;
            }

            if (doc.defaultAmount !== null) {
                this._defaultAmount.value = doc.defaultAmount;
            }

            if (doc.minAmount !== null) {
                this._minAmount.value = doc.minAmount;
            }

            if (doc.defaultExtWFlowAmount) {
                this._defaultExtWFlowAmount.value = doc.defaultExtWFlowAmount
            }
        }
    }

    public updateAgentsAmountRanges() {
        const paymentError: ToRef<LightPaymentPidResponseError> = AppController.getInstance().paymentService.paymentError;

        if (paymentError.value.data && paymentError.value.data.ranges) {
            if (this._minAmountEnabled.value) {
                this.agentsAmountRanges.value = this.calculateMinMaxAmountRangesByFirebase(paymentError.value.data.ranges);
            } else {
                this.agentsAmountRanges.value = paymentError.value.data.ranges;
            }
        }
    }

    private calculateMinMaxAmountRangesByFirebase(agentsAmountRanges: AgentsAmountRanges) {
        const { buy } = this._account.value!.currency.rates;
        let minAmount = this._minAmount.value * buy;

        if (minAmount > agentsAmountRanges.min!) {
            agentsAmountRanges.min = Math.min(minAmount, agentsAmountRanges.max!);
        }

        return agentsAmountRanges;
    }

    public reset() {
        this.agentsAmountRanges.value = null;
    }

    public validateMinmax() {
        if (!this.agentsAmountRanges.value) return true;

        const {min, max} = this.agentsAmountRanges.value;
        if (!min || !max) return true;

        return !(this.amount.value < min || this.amount.value > max);
    }

    public setMaxAmount = () => {
        const { min, max } = this.minMaxRangesAmount.value;
        if (!min || !max) return false;

        this.amount.value = Math.floor(max);
    }

    @autobind
    async next() {
        const payload: createPaymentPayload = {
            amount: this.amount.value,
            msid: this._paymentService.payment.value!.msid!
        }
        const operation = await this._purchaseRepository.patchEmptyPayment(payload);
        FirebaseService.of().wrongRangeMetricSuccess.checkAndSendAnalyticEvent();
        FirebaseService.of().emptyRangeMetricSuccess.checkAndSendAnalyticEvent();
        return operation;
    }

    destructor() {
        this.reset();
        NewPaymentController.instance = undefined;
    }
}
